import { useEffect, useState } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router';
import {
  BookOpen,
  ClipboardCheck,
  HardHat,
  Menu as MenuIcon,
  Ruler,
  Search,
  X,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.js';
import type { Zakazka } from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import { nastavPoSynchronizaci, useOfflineFronta } from '../lib/offline.js';
import { useMojeNalezy } from '../lib/mojeNalezy.js';
import { Btn, Nacitani } from './ui.js';
import { HledaniDeniku } from '../pages/teren/HledaniDeniku.js';

/**
 * Terénní rozvržení (mistr/stavbyvedoucí, telefon — wireframe 13 §1): jeden
 * sloupec, výběr zakázky nahoře (pamatuje si poslední), trvalý offline
 * indikátor a spodní lišta max. 4 položky — Deník / Úkoly+Vady / Výkresy / Menu
 * (Výzvy pod Menu — zakládají se hlavně z deníku).
 * ŽÁDNÉ finance (zeď je na serveru; terénní role je nemá vůbec dostat).
 */

const KLIC_ZAKAZKA = 'helfr.teren.zakazkaId';

/** Terénní uživatel = právo denik.write bez finance.read (mistr). */
export function jeTerenniRole(maPravo: (p: 'denik.write') => boolean, maFinance: boolean): boolean {
  return maPravo('denik.write') && !maFinance;
}

export function ulozenaZakazka(): string | null {
  try {
    return localStorage.getItem(KLIC_ZAKAZKA);
  } catch {
    return null;
  }
}

function ulozZakazku(id: string): void {
  try {
    localStorage.setItem(KLIC_ZAKAZKA, id);
  } catch {
    // private mode apod. — paměť volby je bonus, ne podmínka
  }
}

/** Kontext outletu — vybraná zakázka pro všechny terénní stránky. */
export interface TerenKontext {
  zakazka: Zakazka;
}

/**
 * Max. 4 položky (wireframe 13 §1) — Výkresy si mistr otevírá často (pin vady,
 * „podle čeho stavím"), Výzvy se zakládají hlavně z deníku → přesunuty pod Menu.
 */
const POLOZKY = [
  { cesta: '/teren/denik', nazev: 'Deník', Ikona: BookOpen },
  { cesta: '/teren/ukoly', nazev: 'Úkoly a vady', Ikona: ClipboardCheck },
  { cesta: '/teren/vykresy', nazev: 'Výkresy', Ikona: Ruler },
  { cesta: '/teren/menu', nazev: 'Menu', Ikona: MenuIcon },
];

export function TerenLayout() {
  const { me, nacitani, maPravo } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const fronta = useOfflineFronta();
  const [zakazkaId, setZakazkaId] = useState<string | null>(ulozenaZakazka);
  const [hledam, setHledam] = useState(false);
  // Notifikační tečka v Menu: přibyl zamítnutý nález (zpětná vazba mistrovi).
  const { noveZamitnute } = useMojeNalezy(!!me);

  // Po auto-syncu fronty obnovit terénní data (deník, úkoly, headcount…).
  useEffect(() => {
    nastavPoSynchronizaci(() => void queryClient.invalidateQueries());
    return () => nastavPoSynchronizaci(null);
  }, [queryClient]);

  const { data: zakazky, isLoading } = useQuery({
    queryKey: ['zakazky'],
    queryFn: () => api.get<Zakazka[]>('/api/projects'),
    enabled: !!me,
    staleTime: 10 * 60 * 1000,
  });

  if (nacitani) return <Nacitani text="Ověřuji přihlášení…" />;
  if (!me) return <Navigate to="/login" state={{ od: location.pathname }} replace />;
  if (!maPravo('denik.write')) return <Navigate to="/zakazky" replace />;

  if (isLoading || !zakazky) return <Nacitani text="Načítám zakázky…" />;
  if (zakazky.length === 0) {
    // Plnohodnotný prázdný stav — holá věta vypadá jako rozbitá aplikace.
    return (
      <div className="mx-auto flex min-h-screen max-w-2xl flex-col items-center justify-center gap-4 bg-bg p-6 text-center">
        <span className="flex size-16 items-center justify-center rounded-full bg-primary-tint text-primary">
          <HardHat size={30} strokeWidth={1.75} aria-hidden="true" />
        </span>
        <div>
          <p className="text-xl font-bold">Zatím tu není žádná stavba</p>
          <p className="mt-1 text-base text-text-secondary">
            Kancelář vám ještě nezaložila zakázku. Až ji založí, objeví se tady sama.
          </p>
        </div>
        <Btn onClick={() => window.location.reload()}>Zkusit znovu</Btn>
        {maPravo('projekty.write') && (
          <Link to="/zakazky" className="font-semibold text-primary underline underline-offset-2">
            Založit zakázku v kanceláři →
          </Link>
        )}
      </div>
    );
  }

  const zakazka = zakazky.find((z) => z.id === zakazkaId) ?? zakazky[0];
  if (!zakazka) return null; // délka ošetřena výše — jen pro typovou jistotu

  return (
    <div className="mx-auto flex min-h-screen max-w-2xl flex-col bg-bg pb-24">
      <header className="sticky top-0 z-40 border-b border-border bg-surface px-4 py-2">
        <div className="flex items-center justify-between gap-3">
          {zakazky.length > 1 ? (
            <select
              aria-label="Zakázka"
              className="min-h-12 min-w-0 flex-1 truncate rounded-md border border-border bg-surface px-2 text-lg font-semibold transition-colors duration-100 hover:border-border-strong focus:border-primary"
              value={zakazka.id}
              onChange={(e) => {
                setZakazkaId(e.target.value);
                ulozZakazku(e.target.value);
              }}
            >
              {zakazky.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.nazev}
                </option>
              ))}
            </select>
          ) : (
            <div className="truncate text-lg font-semibold">{zakazka.nazev}</div>
          )}
          <button
            type="button"
            aria-label="Hledání v deníku"
            className="flex min-h-12 min-w-12 shrink-0 items-center justify-center rounded-lg text-text-secondary transition-colors duration-100 hover:bg-bg hover:text-text-primary"
            onClick={() => setHledam(true)}
          >
            <Search size={20} strokeWidth={2} aria-hidden="true" />
          </button>
          <OfflineIndikator ceka={fronta.ceka} online={fronta.online} />
        </div>
        {fronta.konflikty.length > 0 && (
          <div
            role="alert"
            className="mt-2 rounded-md border border-error-border bg-error-bg p-2 text-base text-error-text"
          >
            {fronta.konflikty.length}{' '}
            {fronta.konflikty.length === 1
              ? 'zápis se nepodařilo uložit'
              : fronta.konflikty.length < 5
                ? 'zápisy se nepodařilo uložit'
                : 'zápisů se nepodařilo uložit'}{' '}
            — ťukněte na Menu a vyřešte je.
          </div>
        )}
      </header>

      <main className="flex-1 p-4">
        <Outlet context={{ zakazka } satisfies TerenKontext} />
      </main>

      <nav
        aria-label="Terénní navigace"
        className="fixed inset-x-0 bottom-0 z-40 mx-auto flex max-w-2xl border-t border-border bg-surface"
      >
        {POLOZKY.map((p) => (
          <NavLink
            key={p.cesta}
            to={p.cesta}
            className={({ isActive }) =>
              `relative flex min-h-16 flex-1 flex-col items-center justify-center gap-1 px-1 text-sm font-semibold transition-colors duration-100 ${
                isActive ? 'bg-primary-tint text-primary' : 'text-text-secondary hover:bg-bg'
              }`
            }
          >
            <p.Ikona size={22} strokeWidth={1.75} aria-hidden="true" />
            {p.nazev}
            {p.cesta === '/teren/menu' && noveZamitnute > 0 && (
              <span
                role="status"
                aria-label={`Nové zamítnuté nálezy: ${noveZamitnute}`}
                className="absolute top-2 right-1/4 size-3 rounded-full bg-error"
              />
            )}
          </NavLink>
        ))}
      </nav>

      {/* Hledání v deníku — popup přes obsah (není součást zápisu dne). */}
      {hledam && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-overlay p-4 pt-16">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Hledání v deníku"
            className="w-full max-w-md"
          >
            <div className="mb-2 flex justify-end">
              <button
                type="button"
                aria-label="Zavřít hledání"
                className="flex min-h-12 min-w-12 items-center justify-center rounded-lg bg-surface text-text-secondary shadow-md hover:bg-bg"
                onClick={() => setHledam(false)}
              >
                <X size={20} strokeWidth={2} aria-hidden="true" />
              </button>
            </div>
            <HledaniDeniku zakazkaId={zakazka.id} />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Indikátor fronty v hlavičce — jen když je co hlásit: [~] Čeká na odeslání (N)
 * / Bez připojení. V pořádku = nic („✓ Odesláno" vedle rozepsaného dne mátlo —
 * vypadalo jako stav zápisu, ne fronty; detail odesílání je v Menu).
 */
function OfflineIndikator({ ceka, online }: { ceka: number; online: boolean }) {
  if (ceka > 0) {
    return (
      <span className="shrink-0 rounded-full border border-warning-border bg-warning-bg px-3 py-2 text-base font-semibold whitespace-nowrap text-warning-text">
        ~ Čeká na odeslání ({ceka})
      </span>
    );
  }
  if (!online) {
    return (
      <span className="shrink-0 rounded-full bg-fill-disabled px-3 py-2 text-base font-semibold whitespace-nowrap text-text-secondary">
        Bez připojení
      </span>
    );
  }
  return null;
}
